import {
  DEFAULT_CHATBOT_PRIMARY_COLOR,
  type ChatbotPlacement,
  type ChatbotWithIdentity,
} from '../../../types/chatbot';
import {
  resolveRagMaxResults,
  resolveRagMinScore,
} from '../../../utils/ragRetrievalSettings';

export const PLACEMENT_OPTIONS: ChatbotPlacement[] = [
  'TOP_LEFT',
  'TOP_RIGHT',
  'BOTTOM_LEFT',
  'BOTTOM_RIGHT',
];

export function parseCorsAllowedOrigins(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
}

export function areOriginsEqual(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

export function normalizeOptionalText(
  value: string | null | undefined,
): string {
  return value?.trim() ?? '';
}

export function computeChatbotConfigurationDirtyFlags(options: {
  chatbot: ChatbotWithIdentity;
  draftShowPreview: boolean;
  showPreview: boolean;
  isPublic: boolean;
  displayName: string;
  welcomeMessage: string;
  systemPrompt: string;
  maxResults: number;
  minScore: number;
  primaryColor: string;
  placement: ChatbotPlacement;
  origins: string[];
  originalOrigins: string[];
  isLogoDirty: boolean;
}): {
  isPreviewDirty: boolean;
  isPublicDirty: boolean;
  isDisplayNameDirty: boolean;
  isWelcomeDirty: boolean;
  isSystemPromptDirty: boolean;
  isMaxResultsDirty: boolean;
  isMinScoreDirty: boolean;
  isPrimaryColorDirty: boolean;
  isPlacementDirty: boolean;
  isCorsDirty: boolean;
  isAppearanceDirty: boolean;
  isAppearanceCardDirty: boolean;
  isAiConfigurationDirty: boolean;
} {
  const {
    chatbot,
    draftShowPreview,
    showPreview,
    isPublic,
    displayName,
    welcomeMessage,
    systemPrompt,
    maxResults,
    minScore,
    primaryColor,
    placement,
    origins,
    originalOrigins,
    isLogoDirty,
  } = options;

  const isPreviewDirty = draftShowPreview !== showPreview;
  const isPublicDirty = isPublic !== (chatbot.isPublic === true);
  const isDisplayNameDirty =
    displayName !== normalizeOptionalText(chatbot.displayName);
  const isWelcomeDirty =
    welcomeMessage !== normalizeOptionalText(chatbot.welcomeMessage);
  const isSystemPromptDirty =
    systemPrompt !== normalizeOptionalText(chatbot.systemPrompt);
  const isMaxResultsDirty =
    maxResults !== resolveRagMaxResults(chatbot.maxResults);
  const isMinScoreDirty = minScore !== resolveRagMinScore(chatbot.minScore);
  const originalPrimaryColor =
    normalizeOptionalText(chatbot.primaryColor) ||
    DEFAULT_CHATBOT_PRIMARY_COLOR;
  const isPrimaryColorDirty = primaryColor !== originalPrimaryColor;
  const isPlacementDirty = placement !== (chatbot.placement ?? 'BOTTOM_RIGHT');
  const isCorsDirty = !areOriginsEqual(origins, originalOrigins);
  const isAppearanceDirty =
    isDisplayNameDirty ||
    isPrimaryColorDirty ||
    isWelcomeDirty ||
    isPlacementDirty;
  const isAiConfigurationDirty =
    isSystemPromptDirty || isMaxResultsDirty || isMinScoreDirty;

  return {
    isPreviewDirty,
    isPublicDirty,
    isDisplayNameDirty,
    isWelcomeDirty,
    isSystemPromptDirty,
    isMaxResultsDirty,
    isMinScoreDirty,
    isPrimaryColorDirty,
    isPlacementDirty,
    isCorsDirty,
    isAppearanceDirty,
    isAppearanceCardDirty: isAppearanceDirty || isLogoDirty,
    isAiConfigurationDirty,
  };
}

export function placementI18nKey(
  option: ChatbotPlacement,
): 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' {
  if (option === 'TOP_LEFT') {
    return 'topLeft';
  }
  if (option === 'TOP_RIGHT') {
    return 'topRight';
  }
  if (option === 'BOTTOM_LEFT') {
    return 'bottomLeft';
  }
  return 'bottomRight';
}
